import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  parseMarkup,
  serializeSegments,
  extractMentions,
  type Segment,
  type MentionUser,
} from '../utils/mentionMarkup';

export type MentionInputProps = {
  value: string; // Markup format: "@[Name](id)"
  onChange: (value: string, mentions: MentionUser[]) => void;
  fetchSuggestions: (query: string) => Promise<MentionUser[]>;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  maxLength?: number;
  autoFocus?: boolean;
  rows?: number;
};

const EMOJI_OPTIONS = ['😊', '❤️', '👍', '😂', '🎉', '👏', '🔥', '🤔', '👀', '💯'];

export default function MentionInput({
  value,
  onChange,
  fetchSuggestions,
  placeholder = 'Write a comment...',
  disabled = false,
  className = '',
  maxLength = 2000,
  autoFocus = false,
  rows = 3,
}: MentionInputProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<MentionUser[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [suggestionPosition, setSuggestionPosition] = useState({ top: 0, left: 0 });
  const [charCount, setCharCount] = useState(0);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  
  // Save the mention context when showing suggestions
  const mentionContextRef = useRef<{
    textNode: Node;
    atIndex: number;
    queryLength: number;
  } | null>(null);

  // Render segments into the contenteditable div
  const renderSegments = useCallback((segs: Segment[]) => {
    if (!editorRef.current) return;

    const editor = editorRef.current;
    editor.innerHTML = '';

    segs.forEach((segment, index) => {
      if (segment.type === 'text') {
        const textNode = document.createTextNode(segment.text);
        editor.appendChild(textNode);
      } else {
        const span = document.createElement('span');
        span.className = 'mention-token';
        span.contentEditable = 'false';
        span.setAttribute('data-mention-id', segment.id);
        span.setAttribute('data-segment-index', String(index));
        span.textContent = `@${segment.display}`;
        editor.appendChild(span);
      }
    });

    // Show placeholder if empty
    if (editor.textContent === '') {
      editor.setAttribute('data-placeholder', placeholder);
    } else {
      editor.removeAttribute('data-placeholder');
    }
  }, [placeholder]);

  // Track if we're internally updating to prevent render loops
  const isInternalUpdate = useRef(false);
  const lastValue = useRef(value);

  // Parse value into segments when it changes externally (not from our own input)
  useEffect(() => {
    // Only re-render if the value changed externally (not from our own handleInput)
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      lastValue.current = value;
      return;
    }
    
    // Only re-render if value actually changed
    if (value !== lastValue.current) {
      lastValue.current = value;
      const newSegments = parseMarkup(value);
      renderSegments(newSegments);
      
      // Update character count based on visible text
      if (editorRef.current) {
        setCharCount(editorRef.current.textContent?.length || 0);
      }
    }
  }, [value, renderSegments]);

  // Extract segments from the current DOM state
  const extractSegmentsFromDOM = (): Segment[] => {
    if (!editorRef.current) return [];

    const editor = editorRef.current;
    const newSegments: Segment[] = [];
    
    editor.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || '';
        if (text) {
          newSegments.push({ type: 'text', text });
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        if (element.classList.contains('mention-token')) {
          const id = element.getAttribute('data-mention-id') || '';
          const display = element.textContent?.replace('@', '') || '';
          if (id && display) {
            newSegments.push({ type: 'mention', id, display });
          }
        } else {
          // Handle other elements (like divs/spans from paste) - extract their text
          const text = element.textContent || '';
          if (text) {
            newSegments.push({ type: 'text', text });
          }
        }
      }
    });

    return newSegments;
  };

  // Debounced suggestion fetch (must be defined first)
  const fetchSuggestionsDebounced = useCallback(
    debounce(async (query: string) => {
      const results = await fetchSuggestions(query);
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
      setSelectedIndex(0);
    }, 200),
    [fetchSuggestions]
  );

  // Detect if user is typing a mention (must be defined second)
  const detectMentionQuery = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) {
      return;
    }

    const range = selection.getRangeAt(0);
    const textNode = range.startContainer;

    if (textNode.nodeType !== Node.TEXT_NODE) {
      setShowSuggestions(false);
      return;
    }

    const textContent = textNode.textContent || '';
    const cursorOffset = range.startOffset;

    // Find the last @ before cursor
    let atIndex = -1;
    for (let i = cursorOffset - 1; i >= 0; i--) {
      if (textContent[i] === '@') {
        atIndex = i;
        break;
      }
      // Stop at whitespace or newline
      if (/[\s\n]/.test(textContent[i])) {
        break;
      }
    }

    if (atIndex >= 0) {
      const query = textContent.substring(atIndex + 1, cursorOffset);
      // Only show suggestions if query doesn't contain spaces
      if (!/\s/.test(query)) {
        // Save the mention context for later use
        mentionContextRef.current = {
          textNode,
          atIndex,
          queryLength: query.length,
        };
        
        fetchSuggestionsDebounced(query);
        
        // Calculate position for dropdown
        const rect = range.getBoundingClientRect();
        const editorRect = editorRef.current?.getBoundingClientRect();
        if (editorRect) {
          setSuggestionPosition({
            top: rect.bottom - editorRect.top + 5,
            left: rect.left - editorRect.left,
          });
        }
        return;
      }
    }

    setShowSuggestions(false);
  }, [fetchSuggestionsDebounced]);

  // Handle input changes (must be defined third)
  const handleInput = useCallback(() => {
    if (!editorRef.current) return;

    const newSegments = extractSegmentsFromDOM();
    const markup = serializeSegments(newSegments);
    const mentions = extractMentions(newSegments);

    // Update character count (visible text length)
    const textLength = editorRef.current.textContent?.length || 0;
    setCharCount(textLength);

    // Set flag to prevent re-render when value comes back
    isInternalUpdate.current = true;
    onChange(markup, mentions);

    // Check for @ mention trigger
    detectMentionQuery();
  }, [onChange, detectMentionQuery]);

  // Insert a mention at the current cursor position
  const insertMention = useCallback((user: MentionUser) => {
    if (!editorRef.current) {
      return;
    }

    // Use the saved mention context if available
    if (!mentionContextRef.current) {
      return;
    }

    const { textNode, atIndex, queryLength } = mentionContextRef.current;

    // Check if the saved text node is still valid and in the DOM
    let validTextNode = textNode;
    if (!validTextNode || validTextNode.nodeType !== Node.TEXT_NODE || !validTextNode.parentNode) {
      // Try to find a text node in the editor
      const editor = editorRef.current;
      if (editor.firstChild && editor.firstChild.nodeType === Node.TEXT_NODE) {
        validTextNode = editor.firstChild;
      } else {
        return;
      }
    }

    const textContent = validTextNode.textContent || '';

    // Validate atIndex - if the text has changed, recalculate
    let validAtIndex = atIndex;
    if (validAtIndex < 0 || validAtIndex >= textContent.length) {
      // Try to find @ in the text
      validAtIndex = textContent.indexOf('@');
      if (validAtIndex < 0) {
        return;
      }
    }

    // Replace @query with the mention
    const beforeAt = textContent.substring(0, validAtIndex);
    const afterQuery = textContent.substring(validAtIndex + 1 + queryLength);

    // Save parent reference BEFORE we potentially remove the text node
    const parentNode = validTextNode.parentNode;
    if (!parentNode) {
      return;
    }

    // Create mention span
    const mentionSpan = document.createElement('span');
    mentionSpan.className = 'mention-token';
    mentionSpan.contentEditable = 'false';
    mentionSpan.setAttribute('data-mention-id', user.id);
    mentionSpan.textContent = `@${user.display}`;

    // Create text node for after (with space)
    const afterNode = document.createTextNode(` ${afterQuery}`);

    // Handle the insertion based on whether there's text before the mention
    if (beforeAt) {
      // There's text before @ - update the existing text node and insert after it
      validTextNode.textContent = beforeAt;
      parentNode.insertBefore(mentionSpan, validTextNode.nextSibling);
      parentNode.insertBefore(afterNode, mentionSpan.nextSibling);
    } else {
      // No text before @ - remove the old text node and insert mention at the start
      parentNode.removeChild(validTextNode);
      parentNode.insertBefore(mentionSpan, parentNode.firstChild);
      parentNode.insertBefore(afterNode, mentionSpan.nextSibling);
    }

    // Move cursor after the mention
    const selection = window.getSelection();
    if (selection) {
      const newRange = document.createRange();
      newRange.setStart(afterNode, 1);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
    }

    // Clear the saved context
    mentionContextRef.current = null;
    
    setShowSuggestions(false);
    handleInput();
  }, [handleInput]);

  // Insert emoji at cursor position
  const insertEmoji = useCallback((emoji: string) => {
    if (!editorRef.current) return;

    const editor = editorRef.current;
    const selection = window.getSelection();
    
    // Focus editor first to ensure it has focus
    editor.focus();

    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      
      // Delete any selected content
      range.deleteContents();
      
      // Create text node with emoji
      const emojiNode = document.createTextNode(emoji);
      range.insertNode(emojiNode);
      
      // Move cursor after emoji
      range.setStartAfter(emojiNode);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      // No selection, append to end
      const emojiNode = document.createTextNode(emoji);
      editor.appendChild(emojiNode);
      
      // Move cursor to end
      const newRange = document.createRange();
      newRange.setStartAfter(emojiNode);
      newRange.collapse(true);
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(newRange);
      }
    }

    setShowEmojiPicker(false);
    handleInput();
  }, [handleInput]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (showSuggestions && suggestions.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % suggestions.length);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
        } else if (e.key === 'Enter') {
          e.preventDefault();
          insertMention(suggestions[selectedIndex]);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          setShowSuggestions(false);
        }
        return;
      }

      // Handle backspace on mention tokens
      if (e.key === 'Backspace') {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          if (range.collapsed) {
            // Only delete mention if cursor is at position 0 (directly after the mention)
            // This prevents deleting mentions when cursor is elsewhere in the text
            const isAtStart = range.startOffset === 0;
            if (isAtStart) {
              const previousSibling = range.startContainer.previousSibling;
              if (previousSibling && (previousSibling as HTMLElement).classList?.contains('mention-token')) {
                e.preventDefault();
                previousSibling.remove();
                handleInput();
              }
            }
          }
        }
      }
    },
    [showSuggestions, suggestions, selectedIndex, insertMention, handleInput]
  );

  // Auto-focus if requested
  useEffect(() => {
    if (autoFocus && editorRef.current) {
      editorRef.current.focus();
    }
  }, [autoFocus]);

  return (
    <div className={`mention-input-container ${className}`}>
      <div
        ref={editorRef}
        contentEditable={!disabled}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        className="mention-editor"
        style={{
          minHeight: `${rows * 1.5}em`,
          maxHeight: '200px',
          overflowY: 'auto',
        }}
        suppressContentEditableWarning
      />

      {showSuggestions && suggestions.length > 0 && (
        <div
          className="mention-suggestions"
          style={{
            position: 'absolute',
            top: `${suggestionPosition.top}px`,
            left: `${suggestionPosition.left}px`,
          }}
        >
          {suggestions.map((user, index) => (
            <div
              key={user.id}
              className={`mention-suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => insertMention(user)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className="font-medium text-sm">{user.display}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between mt-1">
        {/* Emoji button */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            disabled={disabled}
            className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 border border-gray-300 transition-all hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Add emoji"
          >
            <span className="text-lg">😊</span>
          </button>

          {/* Emoji picker popup */}
          {showEmojiPicker && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-[60]"
                onClick={() => setShowEmojiPicker(false)}
              />
              {/* Picker */}
              <div className="absolute left-0 bottom-full mb-2 bg-white rounded-xl shadow-2xl border border-gray-300 p-4 grid grid-cols-5 gap-3 z-[70] min-w-[240px]">
                {EMOJI_OPTIONS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => insertEmoji(emoji)}
                    className="w-10 h-10 flex items-center justify-center text-2xl hover:bg-gray-100 rounded-lg transition-all duration-100 active:scale-95 hover:scale-110"
                    title={`Insert ${emoji}`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Character count */}
        <div className="text-xs text-gray-500">
          {charCount} / {maxLength}
        </div>
      </div>

      <style>{`
        .mention-input-container {
          position: relative;
          width: 100%;
        }

        .mention-editor {
          width: 100%;
          padding: 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          outline: none;
          font-size: 14px;
          line-height: 1.5;
          white-space: pre-wrap;
          word-wrap: break-word;
        }

        .mention-editor:focus {
          border-color: #ec4899;
          box-shadow: 0 0 0 2px rgba(236, 72, 153, 0.1);
        }

        .mention-editor[data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
        }

        .mention-token {
          color: #1976d2;
          background: rgba(25, 118, 210, 0.08);
          border-radius: 3px;
          padding: 1px 3px;
          font-weight: 500;
          cursor: default;
          user-select: none;
        }

        .mention-suggestions {
          z-index: 100;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          max-height: 200px;
          overflow-y: auto;
          min-width: 200px;
        }

        .mention-suggestion-item {
          padding: 8px 12px;
          cursor: pointer;
          transition: background-color 0.15s;
        }

        .mention-suggestion-item:hover,
        .mention-suggestion-item.selected {
          background-color: #f3f4f6;
        }
      `}</style>
    </div>
  );
}

// Simple debounce utility
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return function (...args: Parameters<T>) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

